import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClientActivitySchema,
  ClientAddressSchema,
  ClientContactSchema,
  ClientSchema,
  ProjectSchema,
  type ClientActivityDto,
  type ClientAddressDto,
  type ClientContactDto,
  type ClientDto,
} from '@taqon/contracts';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { getData, getPage, patchJson, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { MigrationTrace } from '../migration/MigrationTrace';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import { OriginatingLeads } from '../marketing/common';
import { ProjectDialog, ProjectTable } from '../projects/ProjectsPage';
import { ActivityTable, CompleteActivity } from './ActivitiesPage';
import { ActivityDialog } from './ActivityDialog';
import { ClientDialog } from './ClientsPage';
import {
  CLIENTS,
  ConfirmDialog,
  EmployeeName,
  isMasked,
  listLabel,
  PROJECTS,
  RELEASE_ONE_STAGES,
  STAGE_LABELS,
  useCrmLists,
} from './common';
import { EmailLink, PhoneLinks } from '../../shared/ui/contact';

/**
 * One client (`/crm/clients/:id`; ERD §7.5, FRD-CRM-006 to CRM-033): the master record with its
 * lifecycle stage and status actions, its addresses, contacts, activity history (newest first) and
 * projects. Each section and action appears only where the session holds its permission; related
 * records link only where the session may open them.
 */
export function ClientDetailPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const detail = useQuery({
    queryKey: ['crm', 'client', id],
    queryFn: () => getData(`${CLIENTS}/${id}`, ClientSchema),
  });
  const back = (
    <Button component={RouterLink} to="/crm/clients" startIcon={<ArrowBackIcon />}>
      All clients
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the client" />;
  if (detail.isError) {
    return <RecordFailure title="Client" noun="client" error={detail.error} back={back} />;
  }
  return <ClientRecord client={detail.data} back={back} created={params.get('created') === '1'} />;
}

function ClientRecord({
  client,
  back,
  created,
}: {
  client: ClientDto;
  back: ReactNode;
  created: boolean;
}) {
  const session = useSession();
  const lists = useCrmLists();
  const can = (capability: string) => hasCapability(session.data, capability);
  const canChange = can('crm.client:change');
  const [dialog, setDialog] = useState<'edit' | 'stage' | 'status' | null>(null);
  const shown = (value: string | null) =>
    value === null ? '—' : isMasked(value) ? `${value} (masked)` : value;

  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${client.code} — ${client.companyName}`}
        actions={
          <>
            {back}
            {canChange && (
              <Button
                variant="outlined"
                color={client.status === 'active' ? 'warning' : 'primary'}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('status');
                }}
              >
                {client.status === 'active' ? 'Deactivate' : 'Activate'}
              </Button>
            )}
            {canChange && (
              <Button
                variant="outlined"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('stage');
                }}
              >
                Change stage
              </Button>
            )}
            {canChange && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('edit');
                }}
              >
                Edit
              </Button>
            )}
          </>
        }
      />
      {created && (
        <Alert severity="success" variant="outlined">
          Client {client.code} created. The Client ID was generated by the system.
        </Alert>
      )}
      {client.status === 'inactive' && (
        <Alert severity="info" variant="outlined">
          This client is inactive: it stays on existing records and history but cannot be used for
          new activities or projects.
        </Alert>
      )}
      <Section title="Client">
        <Facts
          label="Client details"
          facts={[
            ['Status', <StatusChip key="s" status={client.status} />],
            ['Client ID', client.code],
            ['Company name', client.companyName],
            ['Lifecycle stage', STAGE_LABELS[client.lifecycleStage]],
            ['Account owner', <EmployeeName key="o" id={client.accountOwnerEmployeeId} />],
            ['Industry', listLabel(lists.data?.industry, client.industryCode)],
            ['Client type', listLabel(lists.data?.clientType, client.clientTypeCode)],
            ['Source', listLabel(lists.data?.clientSource, client.sourceCode)],
            ['Website', client.website ?? '—'],
            ['Email', <EmailLink key="e" email={client.email} />],
            ['Phone', <PhoneLinks key="p" phone={client.phone} />],
            ['TRN', shown(client.trn)],
            ['Notes', client.notes ?? '—'],
          ]}
        />
      </Section>
      <Addresses client={client} />
      {can('crm.client_contact:view') && <Contacts client={client} />}
      {can('crm.client_activity:view') && <Activities client={client} />}
      {can('projects.project:view') && <Projects client={client} />}
      <OriginatingLeads clientId={client.id} />
      {/* Contracts, NDAs, proposals, trade licences (FRD-CRM-049). */}
      <DocumentsPanel recordType="crm.client" recordId={client.id} canAttach={canChange} />
      <MigrationTrace recordType="crm.client" recordId={client.id} />
      <RecordHistory recordType="crm.client" recordId={client.id} />

      {dialog === 'edit' && (
        <ClientDialog
          key={`${client.id}:${String(client.version)}`}
          client={client}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'stage' && (
        <StageDialog
          client={client}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'status' && (
        <StatusDialog
          client={client}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </Stack>
  );
}

/** Invalidates everything that shows a client, after a change to it. */
function useClientChanged() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['crm', 'client'] });
    await queryClient.invalidateQueries({ queryKey: ['crm', 'clients'] });
  };
}

/** Moves the client to another lifecycle stage; every change is audited (FRD-CRM-013). */
function StageDialog({ client, onClose }: { client: ClientDto; onClose: () => void }) {
  const session = useSession();
  const changed = useClientChanged();
  const [stage, setStage] = useState<string>(client.lifecycleStage);
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${CLIENTS}/${client.id}`,
        { lifecycleStage: stage },
        ClientSchema,
        session.data?.csrfToken ?? '',
        client.version,
      ),
    onSuccess: async () => {
      await changed();
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title="Change lifecycle stage"
      submitLabel="Change stage"
      canSubmit={stage !== client.lifecycleStage}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        select
        label="Lifecycle stage"
        value={stage}
        onChange={(e) => {
          setStage(e.target.value);
        }}
        helperText="The opportunity stage arrives with opportunity management in a later phase."
      >
        {RELEASE_ONE_STAGES.map((value) => (
          <MenuItem key={value} value={value}>
            {STAGE_LABELS[value]}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}

/** Activates or deactivates the client after a confirmation (FRD-CRM-012). */
function StatusDialog({ client, onClose }: { client: ClientDto; onClose: () => void }) {
  const session = useSession();
  const changed = useClientChanged();
  const next = client.status === 'active' ? 'inactive' : 'active';
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${CLIENTS}/${client.id}`,
        { status: next },
        ClientSchema,
        session.data?.csrfToken ?? '',
        client.version,
      ),
    onSuccess: async () => {
      await changed();
      onClose();
    },
  });
  return (
    <ConfirmDialog
      title={next === 'inactive' ? 'Deactivate client' : 'Activate client'}
      message={
        next === 'inactive'
          ? `Deactivate ${client.companyName}? It stays on existing records and history but can no longer be used for new work.`
          : `Activate ${client.companyName} again for new work?`
      }
      confirmLabel={next === 'inactive' ? 'Deactivate' : 'Activate'}
      pending={save.isPending}
      error={save.isError ? save.error.message : null}
      onConfirm={() => {
        save.mutate();
      }}
      onClose={onClose}
    />
  );
}

// ---------------------------------------------------------------------------------- addresses

const ADDRESS_TYPES = [
  ['registered', 'Registered'],
  ['invoicing', 'Invoicing'],
  ['other', 'Other'],
] as const;

function Addresses({ client }: { client: ClientDto }) {
  const session = useSession();
  const canChange = hasCapability(session.data, 'crm.client:change');
  const [editing, setEditing] = useState<ClientAddressDto | 'new' | null>(null);
  const addresses = useQuery({
    queryKey: ['crm', 'addresses', client.id],
    queryFn: () => getPage(`${CLIENTS}/${client.id}/addresses?pageSize=200`, ClientAddressSchema),
  });
  return (
    <Section
      title="Addresses"
      actions={
        canChange ? (
          <Button
            size="small"
            onClick={(event) => {
              event.currentTarget.focus();
              setEditing('new');
            }}
          >
            Add address
          </Button>
        ) : undefined
      }
    >
      {addresses.isPending && <LoadingState label="Loading addresses" />}
      {addresses.isError && <ErrorState message={addresses.error.message} />}
      {addresses.data?.items.length === 0 && <EmptyState title="No addresses recorded" />}
      {addresses.data !== undefined && addresses.data.items.length > 0 && (
        <Table size="small" aria-label="Addresses">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Country</TableCell>
              {canChange && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {addresses.data.items.map((address) => (
              <TableRow key={address.id}>
                <TableCell>
                  {ADDRESS_TYPES.find(([value]) => value === address.addressType)?.[1]}
                </TableCell>
                <TableCell>
                  {address.addressLine1}
                  {address.addressLine2 === null ? '' : `, ${address.addressLine2}`}
                </TableCell>
                <TableCell>{address.city}</TableCell>
                <TableCell>{address.country}</TableCell>
                {canChange && (
                  <TableCell align="right">
                    <Button
                      size="small"
                      aria-label={`Edit ${address.addressType} address`}
                      onClick={(event) => {
                        event.currentTarget.focus();
                        setEditing(address);
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing !== null && (
        <AddressDialog
          clientId={client.id}
          address={editing === 'new' ? undefined : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Section>
  );
}

function AddressDialog({
  clientId,
  address,
  onClose,
}: {
  clientId: string;
  address: ClientAddressDto | undefined;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const original = {
    addressType: address?.addressType ?? 'registered',
    addressLine1: address?.addressLine1 ?? '',
    addressLine2: address?.addressLine2 ?? '',
    city: address?.city ?? '',
    country: address?.country ?? '',
  };
  const [values, setValues] = useState(original);
  const set = (field: keyof typeof original) => (value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };
  const body: Record<string, string | null> = {};
  for (const field of Object.keys(values) as (keyof typeof original)[]) {
    const value = values[field].trim();
    if (address === undefined) {
      if (value !== '') body[field] = value;
    } else if (value !== original[field]) {
      body[field] = field === 'addressLine2' && value === '' ? null : value;
    }
  }
  const complete = ['addressLine1', 'city', 'country'].every(
    (field) => values[field as keyof typeof original].trim() !== '',
  );
  const save = useMutation({
    mutationFn: () =>
      address === undefined
        ? postJson(
            `${CLIENTS}/${clientId}/addresses`,
            body,
            ClientAddressSchema,
            session.data?.csrfToken ?? '',
          )
        : patchJson(
            `${CLIENTS}/${clientId}/addresses/${address.id}`,
            body,
            ClientAddressSchema,
            session.data?.csrfToken ?? '',
            address.version,
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm', 'addresses', clientId] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={address === undefined ? 'New address' : 'Edit address'}
      submitLabel={address === undefined ? 'Add address' : 'Save changes'}
      canSubmit={complete && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        select
        label="Address type"
        value={values.addressType}
        onChange={(e) => {
          set('addressType')(e.target.value);
        }}
        helperText="The invoicing address is used where it differs from the registered one."
      >
        {ADDRESS_TYPES.map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      {(
        [
          ['addressLine1', 'Address line 1', true],
          ['addressLine2', 'Address line 2', false],
          ['city', 'City', true],
          ['country', 'Country', true],
        ] as const
      ).map(([field, label, required]) => (
        <TextField
          key={field}
          label={label}
          required={required}
          value={values[field]}
          onChange={(e) => {
            set(field)(e.target.value);
          }}
        />
      ))}
    </FormDialog>
  );
}

// ----------------------------------------------------------------------------------- contacts

type ContactAction = { kind: 'edit' | 'status' | 'primary'; contact: ClientContactDto } | 'new';

function Contacts({ client }: { client: ClientDto }) {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const [action, setAction] = useState<ContactAction | null>(null);
  const contacts = useQuery({
    queryKey: ['crm', 'contacts', client.id, 'all'],
    queryFn: () => getPage(`${CLIENTS}/${client.id}/contacts?pageSize=200`, ClientContactSchema),
  });
  const canChange = can('crm.client_contact:change');
  return (
    <Section
      title="Contacts"
      actions={
        can('crm.client_contact:create') ? (
          <Button
            size="small"
            onClick={(event) => {
              event.currentTarget.focus();
              setAction('new');
            }}
          >
            Add contact
          </Button>
        ) : undefined
      }
    >
      {contacts.isPending && <LoadingState label="Loading contacts" />}
      {contacts.isError && <ErrorState message={contacts.error.message} />}
      {contacts.data?.items.length === 0 && <EmptyState title="No contacts recorded" />}
      {contacts.data !== undefined && contacts.data.items.length > 0 && (
        <Table size="small" aria-label="Contacts">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Designation</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              {canChange && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.data.items.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell>
                  <RecordLink to={`/crm/contacts/${contact.id}`}>{contact.name}</RecordLink>{' '}
                  {contact.isPrimary && (
                    <Chip size="small" color="primary" variant="outlined" label="Primary" />
                  )}
                </TableCell>
                <TableCell>{contact.designation ?? '—'}</TableCell>
                <TableCell>
                  <EmailLink email={contact.email} />
                </TableCell>
                <TableCell>
                  <PhoneLinks phone={contact.phone} />
                </TableCell>
                <TableCell>
                  <StatusChip status={contact.status} />
                </TableCell>
                {canChange && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      aria-label={`Edit ${contact.name}`}
                      onClick={(event) => {
                        event.currentTarget.focus();
                        setAction({ kind: 'edit', contact });
                      }}
                    >
                      Edit
                    </Button>
                    {!contact.isPrimary && contact.status === 'active' && (
                      <Button
                        size="small"
                        aria-label={`Make ${contact.name} primary`}
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setAction({ kind: 'primary', contact });
                        }}
                      >
                        Make primary
                      </Button>
                    )}
                    <Button
                      size="small"
                      color={contact.status === 'active' ? 'warning' : 'primary'}
                      aria-label={`${contact.status === 'active' ? 'Deactivate' : 'Activate'} ${contact.name}`}
                      onClick={(event) => {
                        event.currentTarget.focus();
                        setAction({ kind: 'status', contact });
                      }}
                    >
                      {contact.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {action === 'new' && (
        <ContactDialog
          clientId={client.id}
          onClose={() => {
            setAction(null);
          }}
        />
      )}
      {action !== null && action !== 'new' && action.kind === 'edit' && (
        <ContactDialog
          clientId={client.id}
          contact={action.contact}
          onClose={() => {
            setAction(null);
          }}
        />
      )}
      {action !== null && action !== 'new' && action.kind !== 'edit' && (
        <ContactActionDialog
          clientId={client.id}
          kind={action.kind}
          contact={action.contact}
          onClose={() => {
            setAction(null);
          }}
        />
      )}
    </Section>
  );
}

export function ContactDialog({
  clientId,
  contact,
  onClose,
}: {
  clientId: string;
  contact?: ClientContactDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const original = {
    name: contact?.name ?? '',
    designation: contact?.designation ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
  };
  const [values, setValues] = useState(original);
  const [primary, setPrimary] = useState(false);
  const set = (field: keyof typeof original) => (value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };
  const body: Record<string, string | boolean | null> = {};
  for (const field of Object.keys(values) as (keyof typeof original)[]) {
    const value = values[field].trim();
    if (contact === undefined) {
      if (value !== '') body[field] = value;
    } else if (value !== original[field]) {
      body[field] = field !== 'name' && value === '' ? null : value;
    }
  }
  if (contact === undefined && primary) body.isPrimary = true;
  const save = useMutation({
    mutationFn: () =>
      contact === undefined
        ? postJson(
            `${CLIENTS}/${clientId}/contacts`,
            body,
            ClientContactSchema,
            session.data?.csrfToken ?? '',
          )
        : patchJson(
            `${CLIENTS}/${clientId}/contacts/${contact.id}`,
            body,
            ClientContactSchema,
            session.data?.csrfToken ?? '',
            contact.version,
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm', 'contacts', clientId] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={contact === undefined ? 'New contact' : `Edit contact ${contact.name}`}
      submitLabel={contact === undefined ? 'Add contact' : 'Save changes'}
      canSubmit={values.name.trim() !== '' && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {(
        [
          ['name', 'Name', 'text'],
          ['designation', 'Role / designation', 'text'],
          ['email', 'Email', 'email'],
          ['phone', 'Phone', 'text'],
        ] as const
      ).map(([field, label, type]) => (
        <TextField
          key={field}
          label={label}
          type={type}
          required={field === 'name'}
          value={values[field]}
          onChange={(e) => {
            set(field)(e.target.value);
          }}
        />
      ))}
      {contact === undefined && (
        <TextField
          select
          label="Primary contact"
          value={primary ? 'yes' : 'no'}
          onChange={(e) => {
            setPrimary(e.target.value === 'yes');
          }}
          helperText="Making this the primary contact replaces the current primary contact."
        >
          <MenuItem value="no">No</MenuItem>
          <MenuItem value="yes">Yes</MenuItem>
        </TextField>
      )}
    </FormDialog>
  );
}

/** Make primary, deactivate or activate a contact, after a confirmation. */
export function ContactActionDialog({
  clientId,
  kind,
  contact,
  onClose,
}: {
  clientId: string;
  kind: 'status' | 'primary';
  contact: ClientContactDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const path = `${CLIENTS}/${clientId}/contacts/${contact.id}`;
  const csrf = session.data?.csrfToken ?? '';
  const nextStatus = contact.status === 'active' ? 'inactive' : 'active';
  const run = useMutation({
    mutationFn: () =>
      kind === 'primary'
        ? postJson(`${path}/set-primary`, {}, ClientContactSchema, csrf, {
            expectedVersion: contact.version,
          })
        : patchJson(path, { status: nextStatus }, ClientContactSchema, csrf, contact.version),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm', 'contacts', clientId] });
      onClose();
    },
  });
  const [title, message, label] =
    kind === 'primary'
      ? [
          'Make primary contact',
          `Make ${contact.name} the primary contact? The current primary contact, if any, stops being primary.`,
          'Make primary',
        ]
      : nextStatus === 'inactive'
        ? [
            'Deactivate contact',
            `Deactivate ${contact.name}? The contact stays on existing activities but cannot be chosen for new ones.`,
            'Deactivate',
          ]
        : ['Activate contact', `Activate ${contact.name} again?`, 'Activate'];
  return (
    <ConfirmDialog
      title={title}
      message={message}
      confirmLabel={label}
      pending={run.isPending}
      error={run.isError ? run.error.message : null}
      onConfirm={() => {
        run.mutate();
      }}
      onClose={onClose}
    />
  );
}

// --------------------------------------------------------------------------------- activities

function Activities({ client }: { client: ClientDto }) {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const [action, setAction] = useState<
    { kind: 'edit' | 'complete'; activity: ClientActivityDto } | 'new' | null
  >(null);
  const activities = useQuery({
    queryKey: ['crm', 'activities', 'client', client.id],
    queryFn: () => getPage(`${CLIENTS}/${client.id}/activities?pageSize=200`, ClientActivitySchema),
  });
  const canCreate = can('crm.client_activity:create') && client.status === 'active';
  const canChange = can('crm.client_activity:change');
  return (
    <Section
      title="Activity history"
      actions={
        canCreate ? (
          <Button
            size="small"
            onClick={(event) => {
              event.currentTarget.focus();
              setAction('new');
            }}
          >
            Record activity
          </Button>
        ) : undefined
      }
    >
      {activities.isPending && <LoadingState label="Loading activities" />}
      {activities.isError && <ErrorState message={activities.error.message} />}
      {activities.data?.items.length === 0 && <EmptyState title="No activities recorded" />}
      {activities.data !== undefined && activities.data.items.length > 0 && (
        <ActivityTable
          label="Activity history"
          activities={activities.data.items}
          showClient={false}
          today={businessToday(session.data?.businessTimezone)}
          {...(canChange
            ? {
                actions: (activity: ClientActivityDto) => (
                  <>
                    <Button
                      size="small"
                      aria-label={`Edit activity of ${activity.activityDate}`}
                      onClick={(event) => {
                        event.currentTarget.focus();
                        setAction({ kind: 'edit', activity });
                      }}
                    >
                      Edit
                    </Button>
                    {activity.status === 'open' && (
                      <Button
                        size="small"
                        aria-label={`Complete activity of ${activity.activityDate}`}
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setAction({ kind: 'complete', activity });
                        }}
                      >
                        Complete
                      </Button>
                    )}
                  </>
                ),
              }
            : {})}
        />
      )}
      {action === 'new' && (
        <ActivityDialog
          clientId={client.id}
          onClose={() => {
            setAction(null);
          }}
        />
      )}
      {action !== null && action !== 'new' && action.kind === 'edit' && (
        <ActivityDialog
          clientId={client.id}
          activity={action.activity}
          onClose={() => {
            setAction(null);
          }}
        />
      )}
      {action !== null && action !== 'new' && action.kind === 'complete' && (
        <CompleteActivity
          activity={action.activity}
          onClose={() => {
            setAction(null);
          }}
        />
      )}
    </Section>
  );
}

// ----------------------------------------------------------------------------------- projects

function Projects({ client }: { client: ClientDto }) {
  const session = useSession();
  const [creating, setCreating] = useState(false);
  const projects = useQuery({
    queryKey: ['projects', 'client', client.id],
    queryFn: () =>
      getPage(`${PROJECTS}?pageSize=200&sort=code&filter[client_id]=${client.id}`, ProjectSchema),
  });
  const canCreate =
    hasCapability(session.data, 'projects.project:create') && client.status === 'active';
  return (
    <Section
      title="Projects"
      actions={
        canCreate ? (
          <Button
            size="small"
            onClick={(event) => {
              event.currentTarget.focus();
              setCreating(true);
            }}
          >
            New project for this client
          </Button>
        ) : undefined
      }
    >
      {projects.isPending && <LoadingState label="Loading projects" />}
      {projects.isError && <ErrorState message={projects.error.message} />}
      {projects.data?.items.length === 0 && <EmptyState title="No projects for this client" />}
      {projects.data !== undefined && projects.data.items.length > 0 && (
        <ProjectTable projects={projects.data.items} label="Client projects" />
      )}
      {creating && (
        <ProjectDialog
          client={client}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
    </Section>
  );
}
